import { useState, useRef, useEffect } from "react";
import emailjs from "@emailjs/browser";
import { MapPinIcon, PhoneIcon, EnvelopeIcon } from "@heroicons/react/24/outline";

const ContactForm = () => {
  const formRef = useRef(null);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
    marketing: false,
    websiteDevelopment: false
  });

  const [status, setStatus] = useState({ loading: false, success: false, error: false });

  useEffect(() => {
    if (status.success) {
      const timer = setTimeout(() => setStatus({ loading: false, success: false, error: false }), 4000);
      return () => clearTimeout(timer);
    }
  }, [status.success]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, success: false, error: false });

    try {
      await emailjs.sendForm(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        formRef.current,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );
      setStatus({ loading: false, success: true, error: false });
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        message: "",
        marketing: false, websiteDevelopment: false
      });
    } catch (error) {
      setStatus({ loading: false, success: false, error: true });
    }
  };

  return (
    <section className="min-h-screen py-20 px-4 bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
      <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-12">

        {/* LEFT INFO */}
        <div className="bg-white/80 backdrop-blur-xl border border-white/50 rounded-3xl p-10 shadow-2xl space-y-8">
          <h2 className="text-4xl lg:text-5xl font-black bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
            Let’s Talk
          </h2>
          <div className="space-y-6">
            <InfoItem icon={MapPinIcon} text="Nikat Mata Mandir, Poonam Vihar, Moradabad, India" />
            <InfoItem icon={PhoneIcon} text="+91 8755250061" />
            <InfoItem icon={EnvelopeIcon} text="digitalcantoinfo@gmail.com" />
          </div>
        </div>

        {/* FORM */}
        <div className="bg-white/80 backdrop-blur-xl border border-white/50 rounded-3xl p-10 shadow-2xl">
          <h3 className="text-3xl font-bold text-gray-800 mb-8">Send a Message</h3>

          <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input name="firstName" value={formData.firstName} onChange={handleChange} placeholder="First Name" />
              <Input name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Last Name" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input name="email" value={formData.email} onChange={handleChange} placeholder="Email Address" type="email" />
              <Input name="phone" value={formData.phone} onChange={handleChange} placeholder="Phone Number" />
            </div>

            <div className="space-y-3">
              <p className="text-gray-700 font-medium">Please check the services you need:</p>
              <div className="flex gap-6">
                <label className="flex items-center cursor-pointer">
                  <input type="checkbox" name="marketing" checked={formData.marketing} onChange={handleChange} className="mr-2 w-5 h-5 accent-indigo-600" />
                  Digital marketing
                </label>
                <label className="flex items-center cursor-pointer">
                  <input type="checkbox" name="websiteDevelopment" checked={formData.websiteDevelopment} onChange={handleChange} className="mr-2 w-5 h-5 accent-indigo-600" />
                  Web development
                </label>
              </div>
            </div>

            <textarea
              name="message"
              placeholder="Tell us about your project..."
              value={formData.message}
              onChange={handleChange}
              required
              className="w-full p-5 border border-gray-200 rounded-2xl bg-white/50 focus:ring-4 focus:ring-indigo-200 focus:outline-none transition-all h-32"
            />

            <button
              disabled={status.loading}
              className="w-full h-14 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-2xl shadow-xl hover:scale-[1.01] transition-all disabled:opacity-50"
            >
              {status.loading ? "Sending..." : "Send Message"}
            </button>
          </form>

          {status.success && <p className="mt-6 p-4 bg-green-100 text-green-800 rounded-2xl text-center">✅ Sent successfully!</p>}
          {status.error && <p className="mt-6 p-4 bg-red-100 text-red-800 rounded-2xl text-center">❌ Error sending message.</p>}
        </div>
      </div>
    </section>
  );
};

// Sub-components for cleaner main component
const InfoItem = ({ icon: Icon, text }) => (
  <div className="flex items-start gap-4 p-4 hover:bg-indigo-50 rounded-2xl transition-all">
    <div className="w-12 h-12 bg-indigo-100 rounded-2xl flex items-center justify-center flex-shrink-0">
      <Icon className="w-6 h-6 text-indigo-600" />
    </div>
    <span className="text-lg text-gray-700 font-medium">{text}</span>
  </div>
);

const Input = ({ name, value, onChange, placeholder, type = "text" }) => (
  <input
    type={type}
    name={name}
    value={value}
    onChange={onChange}
    required
    placeholder={placeholder}
    className="w-full p-4 border border-gray-200 rounded-2xl bg-white/50 focus:ring-4 focus:ring-indigo-200 focus:outline-none transition-all"
  />
);

export default ContactForm;

